import { useMemo } from 'react'

import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { useProductReadContext } from '@context/ProductContext'

import { isTickmillAS, isTickmillSC } from '@utils/companyName.utils'
import { isOne } from '@utils/validations'

import { AccountTypeEnum } from '@model/AccountGroupType'
import { NameDto } from '@model/NameDto'
import { isTMTPlatformType, isTradingViewPlatformType } from '@model/PlatformTypeEnum'
import { TradingAccountInfo } from '@model/TradingAccountInfo'

import { CreateTradingAccountCampaignFormProps } from '../CreateTradingAccountCampaign.types'
import { useTradingAccountCreationOptionsFetch } from './useTradingAccountCreationOptionsFetch'

export const usePlatformData = (
  props: CreateTradingAccountCampaignFormProps,
  tradingAccountCreationOptionsFetchProps: ReturnType<typeof useTradingAccountCreationOptionsFetch>,
  allFieldsDisabled: boolean,
  accountTypes: NameDto[]
) => {
  const { values, account } = props
  const { platformTypes } = tradingAccountCreationOptionsFetchProps
  const { product } = useProductReadContext()
  const { accountInfo, accountInfoLimits } = useAccountInfo()

  const isCampaign = useMemo(
    () => !!values.campaignId && values.campaignId !== '0',
    [values.campaignId]
  )

  const accountFilteredPlatforms = useMemo(
    () =>
      accountInfo
        // we need maxAccountsAllowed check when it's a campaign account (https://tickmill.atlassian.net/browse/WCA-3922)
        .filter((x) => {
          const isLiveLimitZero = (accountInfoLimits?.maxTotalLimit ?? 0) === 0
          if (isCampaign && isLiveLimitZero) {
            return false
          }
          return isCampaign || x.maxAccountsAllowed > 0
        })
        .filter((x) =>
          platformTypes.length ? platformTypes.map((pt) => pt.id).includes(x.platform.id) : true
        )
        .filter((x) => x.product.id === product)
        .filter((x) =>
          isTMTPlatformType(x.platform.id) || isTradingViewPlatformType(x.platform.id)
            ? isTickmillSC(account) || isTickmillAS(account)
            : true
        ),
    [accountInfo, platformTypes, product, account, isCampaign]
  )

  const rearrangedTradingPlatforms: TradingAccountInfo[] = useMemo(
    () =>
      platformTypes
        .map((pt) => accountFilteredPlatforms?.find(({ platform }) => platform.id === pt.id))
        .filter((item): item is TradingAccountInfo => !!item),
    [accountFilteredPlatforms, platformTypes]
  )

  const platformsAllowToOpenAccount = useMemo(
    // for campaign accounts, consider all platforms as allowed, regardless of allowToOpenAccount flag (https://tickmill.atlassian.net/browse/WCA-3922)
    () => {
      if (isCampaign) {
        const isLiveLimitZero = (accountInfoLimits?.maxTotalLimit ?? 0) === 0
        if (isLiveLimitZero) {
          return []
        }
        return rearrangedTradingPlatforms
      }
      return rearrangedTradingPlatforms.filter((x) => x.allowToOpenAccount)
    },
    [rearrangedTradingPlatforms, isCampaign, accountInfoLimits?.maxTotalLimit]
  )
  const platformAllowToOpenAccount = useMemo(
    () => platformsAllowToOpenAccount[0],
    [platformsAllowToOpenAccount]
  )
  const platformsDisallowToOpenAccount = useMemo(
    () => (isCampaign ? [] : rearrangedTradingPlatforms.filter((x) => !x.allowToOpenAccount)),
    [rearrangedTradingPlatforms, isCampaign]
  )

  const isAccountTypeDisabled = useMemo(
    () => !values.currencyId || !values.platformType,
    [values.currencyId, values.platformType]
  )
  const isWalletDisabled = useMemo(() => {
    return !values.platformType || tradingAccountCreationOptionsFetchProps.isLoading
  }, [values.platformType, tradingAccountCreationOptionsFetchProps.isLoading])

  const isLeverageDisabled = useMemo(
    () =>
      !values.currencyId ||
      !values.platformType ||
      !values.accountType ||
      isTMTPlatformType(values.platformType?.id) ||
      isTradingViewPlatformType(values.platformType?.id),
    [values.currencyId, values.platformType, values.accountType]
  )

  const accountTypesData = useMemo(() => {
    const isAccountFromBrazil = account?.addresses?.some(
      (address) => address?.country?.id === 'BRA'
    )
    const classicAccountType = accountTypes.find((at) => at.id === AccountTypeEnum.Classic)
    const nonClassicAccountTypes = accountTypes.filter((at) => at.id !== AccountTypeEnum.Classic)
    const accountTypesFilter = classicAccountType
      ? [classicAccountType, ...nonClassicAccountTypes]
      : nonClassicAccountTypes
    return isAccountFromBrazil ? accountTypesFilter : accountTypes
  }, [account?.addresses, accountTypes])

  return {
    isCampaign,
    isWalletDisabled,
    isLeverageDisabled,
    isAccountTypeDisabled,
    accountTypes: accountTypesData,
    platformsAllowToOpenAccount,
    platformAllowToOpenAccount,
    platformsDisallowToOpenAccount,
    isOnlyOnePlatformsAllowToOpenAccount: isOne(platformsAllowToOpenAccount.length),
  }
}
